import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';

import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';

export interface FeedbackStatusPanelProps {
  variant: 'success' | 'error';
  title: string;
  description: string;
  onClose: () => void;
  /** Só no erro: devolve ao formulário com os valores preservados. */
  onRetry?: () => void;
}

/**
 * Tela de desfecho do modal — substitui o formulário depois da resposta.
 *
 * Recebe o foco ao aparecer: sem isso o foco continuaria no botão "Enviar",
 * que acabou de sumir do DOM, e o leitor de tela não teria como anunciar o
 * resultado. O `aria-live` cobre o caso de o usuário estar navegando com o
 * cursor virtual em outro ponto da página.
 */
export const FeedbackStatusPanel: React.FC<FeedbackStatusPanelProps> = ({
  variant,
  title,
  description,
  onClose,
  onRetry,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const isSuccess = variant === 'success';
  const Icon = isSuccess ? CheckCircleIcon : XCircleIcon;

  React.useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-4 py-8 text-center focus-visible:outline-none"
    >
      <Icon
        size={72}
        weight="fill"
        aria-hidden="true"
        className={isSuccess ? 'text-success' : 'text-error'}
      />
      <p className="mb-0 text-reading font-semibold text-on-surface">{title}</p>
      <p className="mb-0 max-w-prose text-base text-on-surface-low">
        {description}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        {onRetry && (
          <MPFButton variant="primary" size="m" onClick={onRetry}>
            Tentar novamente
          </MPFButton>
        )}
        <MPFButton
          variant={onRetry ? 'outline' : 'primary'}
          size="m"
          onClick={onClose}
        >
          Fechar
        </MPFButton>
      </div>
    </div>
  );
};

export default FeedbackStatusPanel;
